import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Formik, Form, Field, ErrorMessage, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import cn from 'classnames';

import { ContactUsService } from 'service/ContactUsService';
import styles from './styles.module.scss';

interface CooperationFormValues {
  name: string;
  email: string;
  message: string;
}

const initialValues: CooperationFormValues = {
  name: '',
  email: '',
  message: '',
};

const CooperationForm: React.FC = () => {
  const { t } = useTranslation();
  const [isSuccess, setIsSuccess] = useState(false);

  const validationSchema = Yup.object({
    name: Yup.string()
      .trim()
      .required(t('contacts.form.validation.name_required'))
      .min(2, t('contacts.form.validation.name_min'))
      .max(32, t('contacts.form.validation.name_max')),
    email: Yup.string()
      .trim()
      .required(t('contacts.form.validation.email_required'))
      .email(t('contacts.form.validation.email_invalid')),
    message: Yup.string()
      .trim()
      .required(t('contacts.form.validation.message_required'))
      .min(2, t('contacts.form.validation.message_min'))
      .max(128, t('contacts.form.validation.message_max')),
  });

  const handleSubmit = async (
    values: CooperationFormValues,
    { resetForm, setSubmitting }: FormikHelpers<CooperationFormValues>
  ) => {
    try {
      await ContactUsService.sendMessage({
        ...values,
        theme: t('main.cooperation.theme'),
      });
      toast.success(t('main.cooperation.success'));
      resetForm();
      setIsSuccess(true);
      setTimeout(() => setIsSuccess(false), 4000);
    } catch {
      toast.error(t('common.internal_server_error'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className={styles.section}>
      <h2 className={styles.sectionTitle}>{t('main.cooperation.title')}</h2>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        validateOnBlur
        validateOnChange={false}
      >
        {({ isSubmitting, errors, touched }) => (
          <Form className={styles.form} noValidate>
            <div className={styles.fields}>
              <div
                className={cn(styles.field, {
                  [styles.fieldError]: touched.name && errors.name,
                })}
              >
                <label className={styles.fieldLabel} htmlFor="cooperation-name">
                  <span className={styles.labelText}>{t('main.cooperation.name')}</span>
                  <span className={styles.required}>*</span>
                </label>
                <Field
                  id="cooperation-name"
                  type="text"
                  name="name"
                  placeholder={t('main.cooperation.namePlaceholder')}
                  className={styles.input}
                />
                <ErrorMessage name="name" component="div" className={styles.errorMessage} />
              </div>

              <div
                className={cn(styles.field, {
                  [styles.fieldError]: touched.email && errors.email,
                })}
              >
                <label className={styles.fieldLabel} htmlFor="cooperation-email">
                  <span className={styles.labelText}>{t('main.cooperation.email')}</span>
                  <span className={styles.required}>*</span>
                </label>
                <Field
                  id="cooperation-email"
                  type="email"
                  name="email"
                  placeholder={t('main.cooperation.emailPlaceholder')}
                  className={styles.input}
                />
                <ErrorMessage name="email" component="div" className={styles.errorMessage} />
              </div>

              <div
                className={cn(styles.field, {
                  [styles.fieldError]: touched.message && errors.message,
                })}
              >
                <label className={styles.fieldLabel} htmlFor="cooperation-message">
                  <span className={styles.labelText}>{t('main.cooperation.message')}</span>
                  <span className={styles.required}>*</span>
                </label>
                <Field
                  as="textarea"
                  id="cooperation-message"
                  name="message"
                  placeholder={t('main.cooperation.messagePlaceholder')}
                  className={styles.textarea}
                />
                <ErrorMessage name="message" component="div" className={styles.errorMessage} />
              </div>
            </div>
            {isSuccess && (
              <div className={styles.successMessage}>{t('main.cooperation.success')}</div>
            )}
            <button type="submit" className={styles.submitButton} disabled={isSubmitting}>
              {t('main.cooperation.submit')}
            </button>
          </Form>
        )}
      </Formik>
    </section>
  );
};

export default CooperationForm;
